import React, { useEffect, useState } from "react";
import { FiFileText, FiUser } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { FaCloudUploadAlt } from "react-icons/fa";
import { notifyError, notifySuccess } from "./ErrorAlert";
import { openSecureFileUrl } from "../utils/fileAccess";
import { HEADER_MESSAGES } from "../constants/headerMessages";

const EMPTY_FORM = {
  name: "",
  type: "",
  gender: "",
  dob: "",
  idType: "",
  idNumber: "",
  contactNumber: "",
  existingIdProof: "",
  idProof: null,
  idProofDeclarationAccepted: false,
};

const calculateAgeLabel = (dob) => {
  if (!dob) return "";

  const birthDate = new Date(dob);
  const today = new Date();

  let years = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    years--;
  }

  return years >= 0 ? `${years} Years` : "";
};

const getCleanFileName = (url) => {
  if (!url) return "";

  const cleanUrl = url.split("?")[0];
  const decodedUrl = decodeURIComponent(cleanUrl);
  const fullName = decodedUrl.substring(decodedUrl.lastIndexOf("/") + 1);

  const parts = fullName.split("-");

  if (parts.length > 1 && /^\d+$/.test(parts[0])) {
    return parts.slice(1).join("-");
  }

  return fullName;
};

const IndividualModal = ({
  isOpen,
  mode = "edit",
  individual = null,
  token,
  onClose,
  onSaved,
  onDelete,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [digitError, setDigitError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && individual) {
      setForm({
        name: individual.name || "",
        type: individual.type || "",
        gender: individual.gender || "",
        dob: individual.dob ? String(individual.dob).split("T")[0] : "",
        idType: individual.idType || "",
        idNumber: individual.idNumber || "",
        contactNumber: individual.contactNumber || "",
        existingIdProof: individual.idProof || "",
        idProof: null,
        idProofDeclarationAccepted: true,
      });
    } else {
      setForm(EMPTY_FORM);
    }

    setErrors({});
    setDigitError(false);
    setIsDragging(false);
  }, [isOpen, mode, individual]);

  if (!isOpen) return null;

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key !== "idProofDeclarationAccepted" && {
        idProofDeclarationAccepted: false,
      }),
    }));

    setErrors((prev) => {
      if (!prev?.[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name?.trim()) nextErrors.name = true;
    if (!form.type) nextErrors.type = true;
    if (!form.gender) nextErrors.gender = true;
    if (!form.dob) nextErrors.dob = true;

    if (!form.contactNumber) {
      nextErrors.contactNumber = true;
    } else if (form.contactNumber.length !== 10) {
      nextErrors.contactNumber = true;
    }

    if (!form.idProofDeclarationAccepted) {
      nextErrors.idProofDeclarationAccepted = true;
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleIdProofChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      notifyError("ID proof file must be less than 5MB.");
      return;
    }

    setForm((prev) => ({
      ...prev,
      idProof: file,
    }));

    setErrors((prev) => {
      const next = { ...prev };
      delete next.idProof;
      return next;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    handleIdProofChange({
      target: {
        files: e.dataTransfer.files,
      },
    });
  };

  const handleIdProofDelete = () => {
    setForm((prev) => ({
      ...prev,
      idProof: null,
      existingIdProof: "",
      idProofUrl: "",
      idProofDeclarationAccepted: false,
    }));
  };

  const handleSecureLinkOpen = async (event, fileValue) => {
    event.preventDefault();

    try {
      await openSecureFileUrl(fileValue, token);
    } catch (error) {
      console.error("Failed to open secure file:", error);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) {
      notifyError(HEADER_MESSAGES.COMPLETE_REQUIRED_FIELDS, "bottom-center");
      return;
    }

    try {
      const payload = new FormData();

      if (form.idProof) {
        payload.append("idProof_0", form.idProof);
      }

      payload.append(
        "individuals",
        JSON.stringify([
          {
            ...(mode === "edit" ? { savedInfoId: individual.id } : {}),
            name: form.name,
            type: form.type,
            gender: form.gender,
            dob: form.dob,
            idType: form.idType,
            idNumber: form.idNumber,
            contactNumber: form.contactNumber,
            existingIdProof: form.existingIdProof || "",
          },
        ]),
      );

      const url =
        mode === "edit"
          ? `${process.env.REACT_APP_API_URL}/profile/saved-individuals/${individual.id}`
          : `${process.env.REACT_APP_API_URL}/profile/saved-individuals`;

      const response = await fetch(url, {
        method: mode === "edit" ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      if (!response.ok) {
        const errorText = await response.text();

        let message = errorText;

        try {
          message = JSON.parse(errorText)?.error || errorText;
        } catch {}

        throw new Error(
          message ||
            (mode === "edit"
              ? HEADER_MESSAGES.UPDATE_INDIVIDUAL_FAILED
              : HEADER_MESSAGES.SAVE_INDIVIDUAL_FAILED),
        );
      }

      const responseText = await response.text();
      const responseData = responseText ? JSON.parse(responseText) : {};

      const nextSavedIndividuals = responseData.savedIndividuals || [];

      onSaved?.(nextSavedIndividuals);

      window.dispatchEvent(
        new CustomEvent("routeplanner:saved-individuals-updated", {
          detail: {
            savedIndividuals: nextSavedIndividuals,
          },
        }),
      );

      notifySuccess(
        mode === "edit"
          ? HEADER_MESSAGES.INDIVIDUAL_UPDATED
          : HEADER_MESSAGES.SAVE_INDIVIDUAL_SUCCESS,
        "bottom-center",
      );

      onClose?.();
    } catch (error) {
      console.error(
        mode === "edit"
          ? "Update individual failed:"
          : "Create individual failed:",
        error,
      );

      notifyError(
        error.message ||
          (mode === "edit"
            ? HEADER_MESSAGES.UPDATE_INDIVIDUAL_FAILED
            : HEADER_MESSAGES.SAVE_INDIVIDUAL_FAILED),
        "bottom-center",
      );
    }
  };

  return (
    <div className="edit-modal-overlay">
      <div className="edit-individual-modal">
        <div className="edit-modal-header">
          <div className="edit-modal-title-section">
            <div className="edit-modal-icon">
              <FiUser />
            </div>

            <div>
              <h2>
                {mode === "edit" ? "Edit Individual" : "Add New Individual"}
              </h2>

              <p>
                {mode === "edit"
                  ? "Update the individual information below."
                  : "Enter the individual information below."}
              </p>
            </div>
          </div>

          <button type="button" className="edit-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="edit-modal-body">
          <div className="edit-form-group">
            <label>
              Individual Name <span>*</span>
            </label>

            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              className={errors.name ? "error-field" : ""}
            />
          </div>

          <div className="edit-form-group">
            <label>
              Individual Type <span>*</span>
            </label>

            <select
              value={form.type}
              onChange={(e) => updateField("type", e.target.value)}
              className={errors.type ? "error-field" : ""}
            >
              <option value="">Select Individual Type</option>
              <option value="Assessor">Assessor</option>
              <option value="Proctor">Proctor</option>
              <option value="SME">SME</option>
              <option value="Employee">Employee</option>
              <option value="Employee_Assessor">Employee-Assessor</option>
              <option value="Trainer">Trainer</option>
              <option value="Counselor">Counselor</option>
              <option value="Intern">Intern</option>
              <option value="Freelancer">Freelancer</option>
            </select>
          </div>

          <div className="edit-form-group">
            <label>
              Gender <span>*</span>
            </label>

            <select
              value={form.gender}
              onChange={(e) => updateField("gender", e.target.value)}
              className={errors.gender ? "error-field" : ""}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Others">Others</option>
              <option value="Not Disclosed">Prefer not to disclose</option>
            </select>
          </div>

          <div className="edit-form-group">
            <label>
              DOB <span>*</span>
            </label>

            <input
              type="date"
              value={form.dob}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  dob: e.target.value,
                }));

                setErrors((prev) => {
                  if (!prev?.dob) return prev;

                  const next = { ...prev };
                  delete next.dob;
                  return next;
                });
              }}
              className={errors.dob ? "error-field" : ""}
            />
          </div>

          <div className="edit-form-group">
            <label>Age</label>

            <input type="text" readOnly value={calculateAgeLabel(form.dob)} />
          </div>

          <div className="edit-form-group">
            <label>
              Contact Number <span>*</span>
            </label>

            <input
              type="text"
              inputMode="numeric"
              maxLength="10"
              value={form.contactNumber}
              onChange={(e) => {
                const value = e.target.value;

                if (/^\d*$/.test(value)) {
                  setDigitError(false);
                  updateField("contactNumber", value);
                } else {
                  setDigitError(true);
                }
              }}
              onKeyDown={(e) => {
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                  setDigitError(true);
                }
              }}
              className={
                errors.contactNumber || digitError ? "error-field" : ""
              }
            />

            {digitError && (
              <span className="inline-error-below">
                {HEADER_MESSAGES.DIGITS_ONLY}
              </span>
            )}
          </div>

          <div className="edit-form-group">
            <label>
              ID Type 
            </label>

            <select
              value={form.idType}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  idType: e.target.value,
                  idNumber: "",
                  existingIdProof: "",
                  idProofUrl: "",
                  idProof: null,
                  idProofDeclarationAccepted: false,
                }));
              }}
              className={errors.idType ? "error-field" : ""}
            >
              <option value="">Select ID Type</option>
              <option value="Aadhaar">Aadhaar</option>
              <option value="Voter ID">Voter ID</option>
              <option value="Passport">Passport</option>
              <option value="Driving License">Driving License</option>
              <option value="PAN">PAN</option>
              <option value="Other Government ID">Other Government ID</option>
            </select>
          </div>

          <div className="edit-form-group">
            <label>
              ID Number 
            </label>

            <input
              type="text"
              value={form.idNumber}
              onChange={(e) => updateField("idNumber", e.target.value)}
              className={errors.idNumber ? "error-field" : ""}
            />
          </div>

          <div className="edit-proof-section">
            <label>
              ID Proof
            </label>
          </div>

          {form.existingIdProof || form.idProof ? (
            <div className="edit-proof-container">
              <div className="edit-proof-card">
                <div className="edit-proof-left">
                  <div className="edit-proof-file-icon">
                    <FiFileText />
                  </div>

                  <div className="edit-proof-details">
                    {form.existingIdProof ? (
                      <>
                        <a
                          href={form.existingIdProof}
                          onClick={(e) =>
                            handleSecureLinkOpen(e, form.existingIdProof)
                          }
                          className="edit-proof-link"
                        >
                          {getCleanFileName(form.existingIdProof)}
                        </a>

                        <span>Click to view / download</span>
                      </>
                    ) : (
                      <>
                        <strong>{form.idProof?.name}</strong>

                        <span>New file selected</span>
                      </>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="edit-proof-delete-button"
                  onClick={handleIdProofDelete}
                >
                  <MdDelete />
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`edit-upload-box ${isDragging ? "drag-active" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
            >
              <label
                htmlFor="individual-id-proof"
                className="edit-upload-label"
              >
                <FaCloudUploadAlt size={24} />

                <h4>Upload ID Proof</h4>

                <span>Drag and drop or click to upload</span>

                <small>PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)</small>
              </label>

              <input
                type="file"
                id="individual-id-proof"
                hidden
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleIdProofChange}
              />
            </div>
          )}

        <div className="edit-declaration-box">
            <label className="edit-declaration-label">
                <input
                type="checkbox"
                checked={form.idProofDeclarationAccepted}
                onChange={(e) =>
                    updateField("idProofDeclarationAccepted", e.target.checked)
                }
                />

                <span>
                I hereby declare that all the information provided for the
                mentioned individual is accurate and valid.
                </span>
            </label>
        </div>

          {errors.idProofDeclarationAccepted && (
            <p className="error-text">{HEADER_MESSAGES.DECLARATION_REQUIRED}</p>
          )}
        </div>

        <div className="edit-modal-footer">
          {mode === "edit" ? (
            <button
              type="button"
              className="edit-delete-button"
              onClick={onDelete}
            >
              Delete
            </button>
          ) : (
            <div />
          )}

          <div className="edit-footer-right">
            <button
              type="button"
              className="edit-cancel-button"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="button"
              className="edit-update-button"
              onClick={handleSubmit}
            >
              {mode === "edit" ? "Update Individual" : "Save Individual"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualModal;
